import { useEffect, useRef } from "react";
import usePriorityPaste from "../../usePriorityPaste";

// Hook para gerenciar atalhos de teclado e área de transferência do diagrama
export default function useDiagramaAtalhos({
  editMode = false,
  isActive = true,
  selectedNodeId = null,
  addNodeAnexoFiles = () => {},
  handleUndo = () => {},
  handleRedo = () => {},
  copySelection = () => {},
  pasteClipboard = () => {},
  duplicateSelection = () => {},
  selectAll = () => {},
  deleteSelection = () => {},
  handleSalvar = () => {},
}) {
  // Com um equipamento selecionado em modo de edição, o Ctrl+V vai direto para os anexos desse nó
  usePriorityPaste({
    active: Boolean(editMode && isActive && selectedNodeId),
    priority: 1,
    onPasteFiles: (arquivos) => addNodeAnexoFiles?.(selectedNodeId, arquivos),
  });

  // Mantém handlers atualizados sem recriar os listeners de eventos globais
  const keyboardHandlersRef = useRef();
  keyboardHandlersRef.current = {
    handleUndo,
    handleRedo,
    copySelection,
    pasteClipboard,
    duplicateSelection,
    selectAll,
    deleteSelection,
    handleSalvar,
  };

  // Registra atalhos de teclado para desfazer, refazer, copiar, duplicar, selecionar tudo, excluir e salvar
  useEffect(() => {
    if (!editMode || !isActive) return undefined;

    const handleKeyDown = (event) => {
      const alvo = event?.target;
      if (
        alvo?.tagName === "INPUT" ||
        alvo?.tagName === "TEXTAREA" ||
        alvo?.isContentEditable
      ) {
        return;
      }

      const {
        handleUndo: desfazer,
        handleRedo: refazer,
        copySelection: copiar,
        duplicateSelection: duplicar,
        selectAll: selecionarTudo,
        deleteSelection: excluir,
        handleSalvar: salvar,
      } = keyboardHandlersRef?.current || {};

      // Salva o circuito com Ctrl+S ou Cmd+S — preventDefault sempre, mesmo desabilitado (senão
      // o navegador abre o diálogo nativo de "Salvar página" por cima do diagrama)
      if ((event?.ctrlKey || event?.metaKey) && event?.key === "s") {
        event?.preventDefault?.();
        salvar?.();
        return;
      }

      // Desfaz última ação com Ctrl+Z ou Cmd+Z
      if ((event?.ctrlKey || event?.metaKey) && event?.key === "z") {
        event?.preventDefault?.();
        desfazer?.();
        return;
      }

      // Refaz última ação com Ctrl+Y ou Cmd+Y
      if ((event?.ctrlKey || event?.metaKey) && event?.key === "y") {
        event?.preventDefault?.();
        refazer?.();
        return;
      }

      // Copia nós e conexões selecionados com Ctrl+C ou Cmd+C
      if ((event?.ctrlKey || event?.metaKey) && event?.key === "c") {
        copiar?.();
        return;
      }

      // Duplica seleção atual no canvas com Ctrl+D ou Cmd+D
      if ((event?.ctrlKey || event?.metaKey) && event?.key === "d") {
        event?.preventDefault?.();
        duplicar?.();
        return;
      }

      // Seleciona todos os nós e conexões com Ctrl+A ou Cmd+A
      if ((event?.ctrlKey || event?.metaKey) && event?.key === "a") {
        event?.preventDefault?.();
        selecionarTudo?.();
        return;
      }

      // Exclui seleção atual com Backspace ou Delete
      if (event?.key === "Backspace" || event?.key === "Delete") {
        excluir?.();
      }
    };

    window?.addEventListener?.("keydown", handleKeyDown);
    return () => window?.removeEventListener?.("keydown", handleKeyDown);
  }, [editMode, isActive]);

  // Cola nós da área de transferência quando não houver arquivos no evento paste nativo
  useEffect(() => {
    if (!editMode || !isActive) return undefined;

    const handlePaste = (event) => {
      const alvo = event?.target;
      if (
        alvo?.tagName === "INPUT" ||
        alvo?.tagName === "TEXTAREA" ||
        alvo?.isContentEditable
      ) {
        return;
      }
      if ((event?.clipboardData?.files?.length || 0) > 0) return;

      keyboardHandlersRef?.current?.pasteClipboard?.();
    };

    document?.addEventListener?.("paste", handlePaste);
    return () => document?.removeEventListener?.("paste", handlePaste);
  }, [editMode, isActive]);
}
